import { inject, Injectable } from '@angular/core';
import {
  type CreateLearningResourceArticleInput,
  type CreateLearningResourceArticleMutation,
  type CreateLearningResourceArticleMutationVariables,
  type DestroyLearningResourceArticleInput,
  type DestroyLearningResourceArticleMutation,
  type LearningResourceArticle,
  type LearningResourceArticleQuery,
  type LearningResourceArticleQueryVariables,
  type MutationDestroyLearningResourceArticleArgs,
  type UpdateLearningResourceArticleInput,
  type UpdateLearningResourceArticleMutation,
  type UpdateLearningResourceArticleMutationVariables,
} from '@reconcept-platform/shared/types';
import { gql } from 'apollo-angular';
import { map } from 'rxjs';

import { SharedGraphQlService } from '../../../services/shared-graphql.service';
import { assertGraphqlDataNode } from '../../../utils/type-helpers';
import { learningResourceArticleWithContent } from '../learning-resource-article.minimal';
import { learningResourceSetQuery } from './shared-learning-resource.api';

const learningResourceArticleQuery = gql`
  query LearningResourceArticle($learningResourceArticleId: ID!) {
    node(id: $learningResourceArticleId) {
      ... on LearningResourceArticle {
        ...LearningResourceArticleWithContent
      }
    }
  }
  ${learningResourceArticleWithContent}
`;

@Injectable({
  providedIn: 'root',
})
export class SharedLearningResourceArticleApi {
  private readonly sharedGraphQlService = inject(SharedGraphQlService);

  watchLearningResourceArticle(learningResourceArticleId: string) {
    return this.sharedGraphQlService
      .watchQuery<LearningResourceArticleQuery, LearningResourceArticleQueryVariables>({
        query: learningResourceArticleQuery,
        variables: {
          learningResourceArticleId,
        },
      })
      .pipe(map((result) => assertGraphqlDataNode(result, 'LearningResourceArticle') as LearningResourceArticle));
  }

  createLearningResourceArticle(input: CreateLearningResourceArticleInput) {
    return (
      this.sharedGraphQlService
        .mutateV2<CreateLearningResourceArticleMutation, CreateLearningResourceArticleMutationVariables>({
          mutation: gql`
            mutation CreateLearningResourceArticle($input: CreateLearningResourceArticleInput!) {
              createLearningResourceArticle(input: $input) {
                errors {
                  message
                  path
                }
                learningResourceArticle {
                  ...LearningResourceArticleWithContent
                }
              }
            }
            ${learningResourceArticleWithContent}
          `,
          variables: { input },
          refetchQueries: [
            {
              query: learningResourceSetQuery,
              variables: { learningResourceSetId: input.learningResourceSetId },
            },
          ],
        })
        // @ts-expect-error TS18048 possibly undefined | TS18049 possibly null or undefined
        .pipe(map((result) => result.data.createLearningResourceArticle.learningResourceArticle))
    );
  }

  updateLearningResourceArticle(input: UpdateLearningResourceArticleInput) {
    return (
      this.sharedGraphQlService
        .mutateV2<UpdateLearningResourceArticleMutation, UpdateLearningResourceArticleMutationVariables>({
          mutation: gql`
            mutation UpdateLearningResourceArticle($input: UpdateLearningResourceArticleInput!) {
              updateLearningResourceArticle(input: $input) {
                errors {
                  message
                  path
                }
                learningResourceArticle {
                  ...LearningResourceArticleWithContent
                }
              }
            }
            ${learningResourceArticleWithContent}
          `,
          variables: { input },
        })
        // @ts-expect-error TS18048 possibly undefined | TS18049 possibly null or undefined
        .pipe(map((result) => result.data.updateLearningResourceArticle.learningResourceArticle))
    );
  }

  destroyLearningResourceArticle(input: DestroyLearningResourceArticleInput, learningResourceSetId: string) {
    return this.sharedGraphQlService.mutateV2<
      DestroyLearningResourceArticleMutation,
      MutationDestroyLearningResourceArticleArgs
    >({
      mutation: gql`
        mutation DestroyLearningResourceArticle($input: DestroyLearningResourceArticleInput!) {
          destroyLearningResourceArticle(input: $input) {
            errors {
              message
              path
            }
          }
        }
      `,
      variables: {
        input,
      },
      refetchQueries: [
        {
          query: learningResourceSetQuery,
          variables: { learningResourceSetId },
        },
      ],
    });
  }
}
